import { Action, createPropertySelectors, SelectorOptions, State, StateContext, Store } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, throwError } from 'rxjs';

import { StateSliceName } from '../store.models';
import { GameState } from '../game/game.state';
import { RosterState } from '../roster/roster.state';
import { EventState } from '../event/event.state';
import { ApplicationState } from '../application/application.state';

import { SaveEventTreeData, EventTreeBackNavigation, InitializeEventTreeInstance, SelectEventTreeItem, SetEventTreeValue, StartEventTreeService, SetEventTreeError } from './event-tree.actions';
import { eventTreeStateDefaults, getTeamAndPlayersInfo } from './event-tree.utils';
import { IEventTreeStateModel } from './event-tree.model';
import { EventTreeEngine, IData, IEventDef, IGameContext, Semantics } from './event-tree.class';
import { EventTreeService } from './event-tree.service';

@SelectorOptions({ injectContainerState: false })
@State<IEventTreeStateModel>({
    name: StateSliceName.EVENT_TREE,
    defaults: eventTreeStateDefaults,
})
@Injectable()
export class EventTreeState {
    public static selectors = createPropertySelectors<IEventTreeStateModel>(EventTreeState);
    private _eventTreeService = inject(EventTreeService);
    private _store = inject(Store);

    // eslint-disable-next-line complexity
    private _createEventTreeInstance(configuration: IData): EventTreeEngine | undefined {
        const game = this._store.selectSnapshot(GameState.selectors.game);
        const roster = this._store.selectSnapshot(RosterState.selectors.roster);
        const vidParams = this._store.selectSnapshot(GameState.selectors.vidParams);
        const selectedPeriod = this._store.selectSnapshot(GameState.selectors.selectedPeriod);
        const playerEventDefinitions = this._store.selectSnapshot(EventState.selectors.playerEventDefinitions);
        const selectedApplicationId = this._store.selectSnapshot(ApplicationState.selectors.selectedApplicationId);

        if (!vidParams || !game || !roster || !playerEventDefinitions || !selectedApplicationId) {
            return;
        }

        const vidParam = vidParams.find(param => `${param.period}` === selectedPeriod);

        const gameContext: IGameContext = {
            applicationId: parseInt(selectedApplicationId, 10),
            gameId: game.id,
            team1: getTeamAndPlayersInfo(game.homeTeamId, roster),
            team2: getTeamAndPlayersInfo(game.awayTeamId, roster),
            period: parseInt(selectedPeriod ?? '1', 10),
            videoId: vidParam?.vidId ?? '',
        };

        return new EventTreeEngine(
            gameContext,
            configuration,
            playerEventDefinitions as IEventDef[]
        );
    }

    /** Initializes the event tree instance by fetching its configuration. */
    @Action(InitializeEventTreeInstance)
    private _initializeEventTreeInstance(
        ctx: StateContext<IEventTreeStateModel>,
        { gameId, applicationId }: InitializeEventTreeInstance) {
        const { patchState } = ctx;
        patchState({ error: undefined });
        const numericGameId = parseInt(gameId, 10);

        // If game doesn't exist for the tree configuration then we need to perform
        // another POST which will create a new event configuration. Only if second POST
        // fails then we show the erorr banner.
        return this._eventTreeService.getEventTreeConfiguration(numericGameId, applicationId).pipe(
            catchError((error: unknown) => {
                if (error instanceof HttpErrorResponse && error.status === 404) {
                    return this._eventTreeService.postEventTreeConfiguration(numericGameId, applicationId).pipe(
                        catchError((postError: unknown) => {
                            return throwError(() => postError);
                        })
                    );
                }
                return throwError(() => error);
            }),
            map((snapshotData) => {
                const eventTreeInstance = this._createEventTreeInstance(snapshotData);

                return patchState({ eventTreeInstance });
            })
        );
    }

    @Action(StartEventTreeService)
    private _startEventTreeService(
        { getState }: StateContext<IEventTreeStateModel>,
        { eventData }: StartEventTreeService
    ) {
        const state = getState();
        const service = state.eventTreeInstance;

        if (!service) {
            throw new Error('EventTreeService not initialized');
        }

        return service.startNewEvent(eventData);
    }

    @Action(SelectEventTreeItem)
    private _setSelectedEventCategory(
        { getState }: StateContext<IEventTreeStateModel>,
        { event }: SelectEventTreeItem
    ) {
        const service = getState().eventTreeInstance;
        if (!service) {
            throw new Error('EventTreeService not initialized');
        }

        return service.itemClicked(event);
    }

    @Action(SetEventTreeValue)
    private _setValue(
        { getState }: StateContext<IEventTreeStateModel>,
        { semantics, value }: SetEventTreeValue
    ) {
        const state = getState();
        const service = state.eventTreeInstance;

        if (!service) {
            throw new Error('EventTreeService not initialized');
        }

        return service.setValue(semantics as Semantics, value);
    }

    @Action(SaveEventTreeData)
    private _saveEventTreeData({ getState }: StateContext<IEventTreeStateModel>) {
        const service = getState().eventTreeInstance;

        if (!service) {
            throw new Error('EventTreeService not initialized');
        }

        return service.eventSaved();
    }

    @Action(EventTreeBackNavigation)
    private _navigateBackToPreviousEvents({ getState }: StateContext<IEventTreeStateModel>) {
        const service = getState().eventTreeInstance;

        if (!service) {
            throw new Error('EventTreeService not initialized');
        }

        return service.backClicked();
    }

    @Action(SetEventTreeError)
    private _setEventTreeError(ctx: StateContext<IEventTreeStateModel>, { error }: SetEventTreeError) {
        return ctx.patchState({ error });
    }
}
